// src/components/BookingCalendar.js
import React, { useMemo, useCallback } from "react";
import { useBooking } from "../context/BookingContext";
import { TextField, Typography, Box } from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";

// Allow-list specific open dates even if they fall on Mon–Thu
// (format: YYYY-MM-DD, local)
const HOLIDAY_OPEN_DATES = new Set([
  "2025-12-24",
  "2025-12-25",
  "2025-12-31",
  "2026-01-01"
]);

const BookingCalendar = () => {
  const { bookingDetails, setBookingDetails } = useBooking();

  // Controlled value from context
  const pickerValue = useMemo(() => {
    if (!bookingDetails.date) return null;
    const m = moment(bookingDetails.date, "YYYY-MM-DD", true);
    return m.isValid() ? m : null;
  }, [bookingDetails.date]);

  // Disable Mon–Thu, EXCEPT for allow-listed holiday dates
  // MUI DatePicker passes a Moment here because we use AdapterMoment
  const shouldDisableDate = useCallback((m) => {
    if (!m) return false;
    const iso = moment(m).format("YYYY-MM-DD");
    if (HOLIDAY_OPEN_DATES.has(iso)) return false; // force-enable 12/24, 12/25
    const d = moment(m).day(); // 0=Sun ... 6=Sat
    return d === 1 || d === 2 || d === 3; // disable Mon–Wed
  }, []);

  const handleDateChange = useCallback((selected) => {
    if (!selected || !moment(selected).isValid()) {
      // Clear date & any previously selected times
      setBookingDetails((p) => ({ ...p, date: "", time: [] }));
      return;
    }
    const nextDate = moment(selected).format("YYYY-MM-DD");
    setBookingDetails((p) => {
      const dateChanged = p.date !== nextDate;
      return {
        ...p,
        date: nextDate,
        time: dateChanged ? [] : p.time, // clear times when date actually changes
      };
    });
  }, [setBookingDetails]);

  return (
    <Box>
      <Typography variant="h6" fontFamily={"lulo-one"} color="#193e2d" mb={2}>
        Select a Date
      </Typography>

      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DatePicker
          label="Select Date"
          value={pickerValue}
          onChange={handleDateChange}
          minDate={moment().startOf("day")}
          shouldDisableDate={shouldDisableDate}
          renderInput={(params) => (
            <TextField {...params} fullWidth />
          )}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default BookingCalendar;